// ==============================================
// Admin Esri Location Services Page
// ==============================================
//
// The "loop closer" dashboard: device-reported tile usage (attribution,
// from the fleet's Licensing Agents) side by side with Esri-billed usage
// (billing truth, pulled nightly from the ArcGIS portal by the poller),
// and the unattributed gap between them.
//
// Chart conventions follow the dataviz skill: fixed two-slot categorical
// palette validated for the slate-800 surface (device = blue #3987e5,
// Esri = aqua #199e70; CVD ΔE 69.8, contrast ≥3:1), single y-axis, thin
// marks, recessive grid, legend + direct end labels, text in text tokens.

import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GlobeAltIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  SignalSlashIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { api } from '../../services/api';

// ==============================================
// Series palette (validated — see header comment)
// ==============================================

const SERIES = {
  device: { key: 'device', label: 'Device-reported', color: '#3987e5' },
  esri: { key: 'esri', label: 'Esri-billed', color: '#199e70' },
} as const;

const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
} as const;

// ==============================================
// Types
// ==============================================

interface EsriDashboardData {
  account_type: string;
  tiles_this_month: number;
  tiles_last_month: number;
  tiles_change_percent: number;
  cost_this_month_usd: number;
  free_tiles_remaining: number;
  budget_alert: { type: 'warning' | 'critical'; message: string } | null;
  esri_reported_tiles_this_month: number;
  unattributed_tiles_this_month: number;
  poller_configured: boolean;
  last_esri_sync_at: string | null;
}

interface DeviceUsageRow {
  deviceId: string;
  deviceName: string | null;
  serialNumber: string;
  total: number;
}

interface TrendPoint {
  date: string;
  device: number | null;
  esri: number | null;
}

// ==============================================
// Formatting helpers
// ==============================================

const fmt = (n: number) => n.toLocaleString();
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  : n >= 1_000 ? `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`
  : String(n);
const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric', timeZone: 'UTC' });

// ==============================================
// Stat tile — value is the hero; icon chip carries the accent
// ==============================================

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  accent,
  swatch,
}: {
  title: string;
  value: string;
  subtitle?: React.ReactNode;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  accent: string; // hex — chip tint + icon
  swatch?: string; // optional series swatch beside the title
}) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 transition-colors hover:border-slate-600">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-slate-400 flex items-center gap-2">
            {swatch && <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: swatch }} />}
            {title}
          </p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums truncate">{value}</p>
          {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
        </div>
        <div className="p-3 rounded-lg shrink-0" style={{ backgroundColor: `${accent}1a` }}>
          <Icon className="w-6 h-6" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}

// ==============================================
// Crosshair tooltip — text tokens only, swatches carry identity
// ==============================================

function TrendTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: { dataKey: string; value: number | null }[];
  label?: string;
}) {
  if (!active || !payload?.length || !label) return null;
  const byKey = new Map(payload.map((p) => [p.dataKey, p.value]));
  const device = byKey.get('device');
  const esri = byKey.get('esri');
  const gap = typeof esri === 'number' && typeof device === 'number' ? esri - device : null;
  return (
    <div className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1.5">{fmtDay(label)}</p>
      {([SERIES.esri, SERIES.device] as const).map((s) => (
        <p key={s.key} className="flex items-center gap-2 text-slate-200 tabular-nums">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
          <span className="text-slate-400">{s.label}</span>
          <span className="ml-auto pl-4 font-semibold">
            {typeof byKey.get(s.key) === 'number' ? fmt(byKey.get(s.key) as number) : '—'}
          </span>
        </p>
      ))}
      {gap !== null && gap > 0 && (
        <p className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-slate-700 tabular-nums">
          <ExclamationTriangleIcon className="w-3 h-3" style={{ color: STATUS.warning }} />
          <span className="text-slate-400">Unattributed</span>
          <span className="ml-auto pl-4 font-semibold text-slate-200">{fmt(gap)}</span>
        </p>
      )}
    </div>
  );
}

// ==============================================
// Main Page
// ==============================================

const RANGES = [14, 30, 90] as const;

export function AdminEsriPage() {
  const queryClient = useQueryClient();
  // Return address for the device detail page's back button.
  const location = useLocation();
  const backTo = location.pathname + location.search;
  const [configExpanded, setConfigExpanded] = useState(false);
  const [rangeDays, setRangeDays] = useState<(typeof RANGES)[number]>(30);
  const [configForm, setConfigForm] = useState<Record<string, string> | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<EsriDashboardData>({
    queryKey: ['admin', 'esri', 'dashboard'],
    queryFn: () => api.adminGetEsriDashboard(),
    refetchInterval: 300000,
  });

  const { data: trend } = useQuery({
    queryKey: ['admin', 'esri', 'trend', rangeDays],
    queryFn: () => api.adminGetEsriTrend(rangeDays),
  });

  const { data: devices } = useQuery<DeviceUsageRow[]>({
    queryKey: ['admin', 'esri', 'devices'],
    queryFn: () => api.adminGetEsriDeviceUsage(),
  });

  const { data: config } = useQuery({
    queryKey: ['admin', 'esri', 'config'],
    queryFn: () => api.adminGetEsriConfig(),
    enabled: configExpanded,
  });

  const configMutation = useMutation({
    mutationFn: (cfg: Record<string, unknown>) => api.adminUpdateEsriConfig(cfg),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'esri'] });
      setConfigForm(null);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => api.adminEsriSyncNow(),
    onSuccess: (result) => {
      setSyncMessage(result.ok
        ? `Synced ${result.days_upserted} day-rows (${result.services.join(', ') || 'no services'})`
        : `Sync failed: ${result.error ?? 'unknown error'}`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'esri'] });
    },
    onError: (e) => setSyncMessage(`Sync failed: ${(e as Error).message}`),
  });

  // Merge the two daily series into one x-domain (dates either side reported)
  const trendData: TrendPoint[] = useMemo(() => {
    const byDate = new Map<string, TrendPoint>();
    for (const p of trend?.device ?? []) {
      byDate.set(p.date, { date: p.date, device: p.tiles, esri: null });
    }
    for (const p of trend?.esri ?? []) {
      const row = byDate.get(p.date) ?? { date: p.date, device: null, esri: null };
      row.esri = p.tiles;
      byDate.set(p.date, row);
    }
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [trend]);

  const hasTrend = trendData.length > 0;
  const maxDeviceTotal = Math.max(1, ...(devices ?? []).map((d) => d.total));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 text-red-400">
        <ExclamationTriangleIcon className="w-6 h-6 inline mr-2" />
        Failed to load Esri dashboard: {(error as Error).message}
      </div>
    );
  }

  const billed = data?.esri_reported_tiles_this_month ?? 0;
  const attributed = data?.tiles_this_month ?? 0;
  const gap = data?.unattributed_tiles_this_month ?? 0;
  const gapPct = billed > 0 ? Math.round((gap / billed) * 100) : 0;
  const gapState = billed === 0 ? 'idle' : gapPct <= 5 ? 'good' : gapPct <= 25 ? 'warning' : 'serious';
  const freeTiles = data?.free_tiles_remaining ?? 0;

  const lastSync = data?.last_esri_sync_at
    ? new Date(data.last_esri_sync_at).toLocaleString('en-NZ', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="space-y-6">
      {/* ---- Header ---- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Esri Location Services</h1>
          <p className="text-slate-400">Device-reported usage vs Esri billing truth — and the gap between them</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.poller_configured ? (
            <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-emerald-500/15 text-emerald-400 rounded-full">
              <CheckCircleIcon className="w-4 h-4" />
              Loop closed{lastSync ? ` · synced ${lastSync}` : ''}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-amber-500/15 text-amber-400 rounded-full">
              <SignalSlashIcon className="w-4 h-4" />
              Poller not configured
            </span>
          )}
          <span className="px-3 py-1 text-xs font-medium bg-purple-500/20 text-purple-400 rounded-full">
            {data?.account_type === 'developer' ? 'Developer Account' : data?.account_type || 'Unknown'}
          </span>
          <button
            onClick={() => { setSyncMessage(null); syncMutation.mutate(); }}
            disabled={syncMutation.isPending || !data?.poller_configured}
            title={data?.poller_configured ? 'Pull latest usage from the Esri portal' : 'Add the credential Client ID/Secret in Configuration first'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            Sync from Esri
          </button>
        </div>
      </div>

      {syncMessage && (
        <div className={`rounded-lg px-4 py-2.5 text-sm border ${syncMessage.startsWith('Sync failed')
          ? 'bg-red-500/10 border-red-500/40 text-red-300'
          : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'}`}>
          {syncMessage}
        </div>
      )}

      {data?.budget_alert && (
        <div className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm border bg-red-500/10 border-red-500/40 text-red-300">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0" style={{ color: STATUS.serious }} />
          {data.budget_alert.message}
        </div>
      )}

      {/* ---- KPI tiles ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Esri-Billed This Month"
          value={fmt(billed)}
          subtitle={data?.poller_configured ? 'Billing truth from the Esri portal' : 'Awaiting poller configuration'}
          icon={GlobeAltIcon}
          accent={SERIES.esri.color}
          swatch={SERIES.esri.color}
        />
        <StatCard
          title="Device-Reported This Month"
          value={fmt(attributed)}
          subtitle="Attributed to fleet devices"
          icon={DevicePhoneMobileIcon}
          accent={SERIES.device.color}
          swatch={SERIES.device.color}
        />
        <StatCard
          title="Unattributed Gap"
          value={billed === 0 ? '—' : fmt(gap)}
          subtitle={
            billed === 0 ? 'No billed usage recorded yet' : (
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{
                  backgroundColor: gapState === 'good' ? STATUS.good : gapState === 'warning' ? STATUS.warning : STATUS.serious,
                }} />
                {gapPct}% of billed usage {gapState === 'good' ? '· healthy' : gapState === 'warning' ? '· mostly legacy fleet' : '· investigate'}
              </span>
            )
          }
          icon={ScaleIcon}
          accent={gapState === 'serious' ? STATUS.serious : gapState === 'warning' ? STATUS.warning : STATUS.good}
        />
        <StatCard
          title="Cost This Month"
          value={`$${(data?.cost_this_month_usd ?? 0).toFixed(2)}`}
          subtitle={`${fmtCompact(freeTiles)} free-tier tiles remaining`}
          icon={CurrencyDollarIcon}
          accent="#9085e9"
        />
      </div>

      {/* ---- Daily trend: device vs billed ---- */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="text-lg font-semibold text-white">Daily Tile Usage</h3>
          <div className="flex items-center gap-4">
            {/* Legend — identity never by color alone; labels in text ink */}
            <div className="flex items-center gap-4 text-xs text-slate-300">
              {([SERIES.esri, SERIES.device] as const).map((s) => (
                <span key={s.key} className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-[3px] rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
            <div className="flex rounded-lg overflow-hidden border border-slate-700">
              {RANGES.map((d) => (
                <button
                  key={d}
                  onClick={() => setRangeDays(d)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${rangeDays === d
                    ? 'bg-slate-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Esri days are UTC and lag a few hours — the two lines converge as attribution catches up; persistent divergence is unattributed usage.
        </p>
        {hasTrend ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trendData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#334155" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDay}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#334155' }}
                tickLine={false}
                minTickGap={28}
              />
              <YAxis
                tickFormatter={(v: number) => fmtCompact(v)}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={44}
              />
              <Tooltip content={<TrendTooltip />} cursor={{ stroke: '#475569', strokeWidth: 1 }} />
              <ReferenceLine y={0} stroke="#334155" />
              <Line
                type="monotone"
                dataKey="esri"
                name={SERIES.esri.label}
                stroke={SERIES.esri.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 2, stroke: '#1e293b' }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="device"
                name={SERIES.device.label}
                stroke={SERIES.device.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 2, stroke: '#1e293b' }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[280px] flex flex-col items-center justify-center text-center gap-2">
            <MapPinIcon className="w-8 h-8 text-slate-600" />
            <p className="text-slate-500 text-sm max-w-md">
              No daily data yet. Device numbers appear as fleet Licensing Agents report usage;
              Esri numbers appear after the first poller sync (Configuration → credentials, then “Sync from Esri”).
            </p>
          </div>
        )}
      </div>

      {/* ---- Top devices ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Top Devices</h3>
          {(devices?.length ?? 0) > 0 ? (
            <ul className="space-y-3">
              {(devices ?? []).slice(0, 10).map((d) => (
                <li key={d.deviceId} className="group" title={`${d.deviceName ?? d.serialNumber}: ${fmt(d.total)} tiles`}>
                  <div className="flex items-baseline justify-between gap-3 text-sm mb-1">
                    {/* No title here on purpose: the row already carries the
                        tile-count tooltip, and this is where you hover. */}
                    <Link
                      to={`/admin/devices/${d.deviceId}`}
                      state={{ from: backTo }}
                      className="text-slate-300 truncate hover:text-cyan-400 hover:underline"
                    >
                      {d.deviceName ?? d.serialNumber ?? d.deviceId}
                    </Link>
                    <span className="text-slate-200 font-medium tabular-nums shrink-0">{fmt(d.total)}</span>
                  </div>
                  <div className="h-2 rounded bg-slate-700/60 overflow-hidden">
                    <div
                      className="h-full rounded-r transition-all group-hover:brightness-125"
                      style={{ width: `${Math.max(2, (d.total / maxDeviceTotal) * 100)}%`, backgroundColor: SERIES.device.color }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500 text-sm py-8 text-center">
              No device usage reported yet — appears as fleet devices sync.
            </p>
          )}
        </div>

        {/* ---- Usage by user ---- */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Usage by User</h3>
          <p className="text-slate-500 text-sm py-8 text-center">
            Per-user rollups appear once devices report under the new metering —
            open a customer's profile for their per-device breakdown meanwhile.
          </p>
        </div>
      </div>

      {/* ---- Configuration ---- */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700">
        <button
          onClick={() => setConfigExpanded((v) => !v)}
          className="w-full flex items-center justify-between p-6 text-left"
        >
          <span className="flex items-center gap-2 text-white font-semibold">
            <Cog6ToothIcon className="w-5 h-5 text-slate-400" />
            Configuration
          </span>
          {configExpanded ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
        </button>
        {configExpanded && config && (
          <div className="px-6 pb-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {([
                ['cost_per_1000_tiles_usd', 'Cost per 1,000 tiles (USD)'],
                ['free_tiles_per_month', 'Free tiles per month'],
                ['monthly_budget_usd', 'Monthly budget (USD)'],
              ] as const).map(([key, label]) => (
                <label key={key} className="block">
                  <span className="text-xs text-slate-400">{label}</span>
                  <input
                    type="number"
                    className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 focus:outline-none"
                    value={configForm?.[key] ?? String(config[key] ?? '')}
                    onChange={(e) => setConfigForm((f) => ({ ...(f ?? {}), [key]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
            <div>
              <p className="flex items-center gap-2 text-sm text-slate-300 font-medium mb-2">
                <KeyIcon className="w-4 h-4 text-slate-400" />
                Esri usage poller credentials
              </p>
              <p className="text-xs text-slate-500 mb-3">
                From the ArcGIS credential item's page (Client ID / Client Secret). Used server-side only,
                to pull billed usage nightly (02:17 UTC) and on demand. The secret is never displayed once saved.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-slate-400">Client ID</span>
                  <input
                    type="text"
                    autoComplete="off"
                    className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 focus:outline-none font-mono"
                    value={configForm?.client_id ?? String(config.client_id ?? '')}
                    onChange={(e) => setConfigForm((f) => ({ ...(f ?? {}), client_id: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-400">Client Secret</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder={config.client_secret === '__set__' ? '•••••••• (saved — enter to replace)' : 'Enter secret…'}
                    className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500 focus:outline-none font-mono"
                    value={configForm?.client_secret ?? ''}
                    onChange={(e) => setConfigForm((f) => ({ ...(f ?? {}), client_secret: e.target.value }))}
                  />
                </label>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (!configForm) return;
                  const payload: Record<string, unknown> = {};
                  for (const [k, v] of Object.entries(configForm)) {
                    if (v === '') continue;
                    payload[k] = ['cost_per_1000_tiles_usd', 'free_tiles_per_month', 'monthly_budget_usd'].includes(k) ? Number(v) : v;
                  }
                  configMutation.mutate(payload);
                }}
                disabled={!configForm || configMutation.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {configMutation.isPending ? 'Saving…' : 'Save configuration'}
              </button>
              {configForm && (
                <button
                  onClick={() => setConfigForm(null)}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
                >
                  Discard
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
